'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getEnvironmentConfigFromEnv } from '@repo/api';
import { createExperiments, type ExperimentsClient } from '@repo/experiments';
import { ExperimentsProvider as Provider } from '@repo/experiments/react';
import { getAnonymousId } from '@/lib/anon-id';
import { getFirebaseAuth } from '@/lib/auth/firebase';
import { useStore } from '@/providers/StoreProvider';

const ENV = process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'production' : 'development';

/** The signed-in account, or null (logged out, or a guest's anonymous sign-in). */
function account() {
  try {
    const user = getFirebaseAuth().currentUser;
    return user && !user.isAnonymous ? user : null;
  } catch {
    return null;
  }
}

/**
 * A/B testing: one experiments client per page load (browser only; the server always renders the
 * default experience, so variants are never baked into cached HTML).
 */
export function ExperimentsProvider({ children }: { children: ReactNode }) {
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const loggedIn = useRef(isLoggedIn);
  loggedIn.current = isLoggedIn;

  const [client] = useState<ExperimentsClient | null>(() =>
    typeof window === 'undefined'
      ? null
      : createExperiments({
          baseUrl: getEnvironmentConfigFromEnv(ENV).endpoints.messenger,
          platform: 'web',
          getSubjectId: getAnonymousId,
          getUid: () => account()?.uid ?? null,
          getAttributes: () => ({
            logged_in: loggedIn.current,
            device: window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop',
          }),
          getIdToken: async () => (await account()?.getIdToken()) ?? null,
          debug: ENV !== 'production',
        }),
  );

  // Logging in or out changes `logged_in`, which targeting reads: ask again.
  const askedFor = useRef(isLoggedIn);
  useEffect(() => {
    if (askedFor.current === isLoggedIn) return;
    askedFor.current = isLoggedIn;
    void client?.refresh();
  }, [client, isLoggedIn]);

  // Whenever an account is signed in, link this browser to it (sent once per account).
  useEffect(() => {
    if (!client) return;
    try {
      return onAuthStateChanged(getFirebaseAuth(), user => {
        if (user && !user.isAnonymous) void client.identify();
      });
    } catch {
      return;
    }
  }, [client]);

  return client ? <Provider client={client}>{children}</Provider> : <>{children}</>;
}
